@import '@fontsource-variable/inter';
@import './tokens.css';

* { box-sizing: border-box; }
html, body, #root { height: 100%; margin: 0; }
body {
  background: var(--bg);
  color: var(--text);
  font: var(--fw) var(--fs) / 1.2 var(--font);
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}
button, input, textarea, select { font: inherit; color: inherit; }
a { color: inherit; text-decoration: none; }
svg { flex-shrink: 0; }
::selection { background: rgba(0, 153, 255, 0.35); }

/* Texto que no cabe: «…» y el texto entero en el tooltip (regla d). */
.ellipsis { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.muted { color: var(--text-2); }
.dim { color: var(--text-3); }
.mono { font-family: var(--mono); font-size: 11px; }
.grow { flex: 1 1 auto; min-width: 0; }
.row { display: flex; align-items: center; gap: 10px; min-width: 0; }
.col { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.sep { height: 1px; background: var(--border); flex-shrink: 0; }

/* —— Botones (ficha 01/02: 30 px de alto, radio 8, relleno 0 × 10) —— */
.btn {
  height: var(--h-control);
  padding: 0 10px;
  border-radius: var(--r-control);
  border: 0;
  background: var(--bg-selected);
  color: var(--text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn:hover { background: #262626; }
.btn:disabled { opacity: 0.4; cursor: default; }
.btn.primary { background: var(--accent); }
.btn.primary:hover { background: var(--accent-hover); }
.btn.field { background: var(--bg-field); }
.btn.field:hover { background: var(--bg-field-hover); }
.btn.ghost { background: transparent; color: var(--text-2); }
.btn.ghost:hover { background: var(--bg-selected); color: var(--text); }
.btn.danger { background: var(--danger-soft); color: var(--danger); }
.btn.icon { width: var(--h-control); padding: 0; background: transparent; color: var(--icon); }
.btn.icon:hover { background: var(--bg-selected); color: var(--text); }
.btn.small { height: 24px; padding: 0 8px; border-radius: 6px; }

/* —— Campos —— */
.input {
  height: var(--h-control);
  border-radius: var(--r-control);
  border: 0;
  background: var(--bg-field);
  padding: 0 10px;
  outline: none;
  min-width: 0;
  width: 100%;
}
.input:focus { box-shadow: inset 0 0 0 1px var(--accent); }
.input::placeholder { color: var(--text-2); }
textarea.input { height: auto; padding: 8px 10px; resize: vertical; line-height: 1.5; }
select.input { appearance: none; padding-right: 26px; background-image: linear-gradient(45deg, transparent 50%, var(--text-2) 50%), linear-gradient(135deg, var(--text-2) 50%, transparent 50%); background-position: calc(100% - 13px) 13px, calc(100% - 9px) 13px; background-size: 4px 4px; background-repeat: no-repeat; }
.search { position: relative; display: flex; align-items: center; }
.search svg { position: absolute; left: 10px; color: var(--text-2); pointer-events: none; }
.search .input { padding-left: 27px; }
.label { color: var(--text-label); }
.field-row { display: grid; grid-template-columns: 140px minmax(0, 1fr); align-items: center; gap: 10px; min-height: 40px; }
.check { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; color: var(--text-label); }
.check input { accent-color: var(--accent); }
.error { color: var(--danger); }
.hint { color: var(--text-2); line-height: 1.5; }

/* —— Distintivos —— */
.badge { display: inline-flex; align-items: center; height: 16px; padding: 2px 4px; border-radius: var(--r-badge); background: var(--bg-badge); color: rgba(255, 255, 255, 0.8); font-size: 10px; line-height: 12px; flex-shrink: 0; }
.badge.blue { background: var(--bg-accent-soft); color: var(--accent); }
.badge.green { background: rgba(51, 204, 119, 0.1); color: var(--ok); }
.badge.amber { background: rgba(255, 170, 51, 0.1); color: var(--warn); }
.badge.red { background: var(--danger-soft); color: var(--danger); }
.dot { width: 6px; height: 6px; border-radius: 50%; background: var(--text-3); flex-shrink: 0; }
.dot.up { background: var(--ok); }
.dot.down { background: var(--danger); }

/* —— Menús (ficha: fondo #242424, radio 13, relleno 5, filas de 30) —— */
.menu {
  position: fixed;
  z-index: var(--z-menus);
  background: var(--bg-menu);
  border-radius: var(--r-menu);
  box-shadow: var(--shadow-menu);
  padding: 5px;
  min-width: 160px;
  max-width: 320px;
}
.menu-item {
  height: 30px;
  width: 100%;
  border: 0;
  border-radius: var(--r-control);
  background: transparent;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-align: left;
  color: var(--text);
}
.menu-item:hover:not(:disabled), .menu-item:focus-visible { background: var(--accent); outline: none; }
.menu-item:disabled { color: var(--text-dim); cursor: default; }
.menu-item .shortcut { margin-left: auto; color: var(--text-dim); padding-left: 16px; }
.menu-item.danger { color: var(--danger); }
.menu-item.danger:hover { color: var(--text); background: var(--danger); }
.menu-sep { height: 1px; background: var(--border-menu); margin: 3px 0; }

/* —— Diálogos (radio 18) —— */
.overlay { position: fixed; inset: 0; z-index: var(--z-modals); background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; padding: 16px; }
.dialog { width: 100%; max-width: 400px; max-height: calc(100vh - 32px); overflow: auto; background: var(--bg); border-radius: var(--r-dialog); box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4), 0 0 0 1px var(--border-menu); }
.dialog-head { height: 50px; display: flex; align-items: center; justify-content: space-between; padding: 0 5px 0 15px; font-weight: var(--fw-strong); }
.dialog-body { padding: 0 15px 15px; display: flex; flex-direction: column; gap: 10px; }
.dialog-foot { display: flex; justify-content: flex-end; gap: 10px; padding-top: 5px; }

/* —— Aviso flotante —— */
.toast { position: fixed; z-index: var(--z-tooltip); left: 50%; bottom: 24px; transform: translateX(-50%); background: var(--bg-menu); box-shadow: var(--shadow-menu); border-radius: var(--r-control); padding: 9px 12px; max-width: calc(100vw - 32px); display: flex; gap: 8px; align-items: center; }
.toast.error { color: var(--danger); }

/* —— Pantalla de entrada —— */
.login { height: 100%; display: flex; align-items: center; justify-content: center; padding: 16px; overflow: auto; }
.login-card { width: 100%; max-width: 300px; display: flex; flex-direction: column; gap: 10px; }
.login-card h1 { font-size: 20px; line-height: 28px; font-weight: var(--fw-strong); margin: 0 0 6px; }
.logo { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; color: var(--text); flex-shrink: 0; }
.login-logo { display: flex; color: var(--text); margin-bottom: 14px; }

/* —— Panel de proyectos (ficha 01) —— */
.dash { display: grid; grid-template-columns: var(--sidebar-w) minmax(0, 1fr); height: 100%; }
.sidebar { background: var(--bg); border-right: 1px solid var(--border); display: flex; flex-direction: column; padding: 10px; gap: 0; min-height: 0; z-index: var(--z-ui); }
.sidebar .scroll { flex: 1 1 auto; min-height: 0; overflow: auto; display: flex; flex-direction: column; }
.ws-button { height: 30px; border: 0; background: transparent; border-radius: var(--r-control); display: flex; align-items: center; gap: 10px; padding: 0; cursor: pointer; width: 100%; color: var(--text); }
.ws-button .chev { margin-left: auto; color: var(--text-3); }
.side-title { padding: 18px 0 10px; color: var(--text); }
.side-item { height: 30px; border-radius: var(--r-control); display: flex; align-items: center; gap: 10px; padding: 0 8px 0 10px; color: var(--text-2); cursor: pointer; border: 0; background: transparent; width: 100%; text-align: left; }
.side-item svg { color: var(--icon); }
.side-item:hover { background: var(--bg-selected); }
.side-item.active { background: var(--bg-selected); color: var(--text); }
.side-item.active svg { color: var(--text); }
.side-item .count { margin-left: auto; font-size: 10px; color: var(--text-3); }
.side-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-top: 10px; border-top: 1px solid var(--border); }
.main { overflow: auto; min-width: 0; }
.main-inner { max-width: 1200px; margin: 0 auto; padding: 33px 40px 40px; }
.main-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 40px; min-width: 0; }
.main-head h1 { font-size: 20px; line-height: 28px; font-weight: var(--fw-strong); margin: 0; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 260px)); gap: 40px; }
.card { display: flex; flex-direction: column; gap: 15px; cursor: pointer; min-width: 0; }
.thumb { aspect-ratio: 260 / 320; border-radius: var(--r-card); background: var(--bg-thumb); position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.thumb .initial { font-size: 48px; font-weight: var(--fw-strong); color: #2a2a2a; }
.card:hover .thumb { box-shadow: 0 0 0 1px var(--border-menu); }
.card-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-width: 0; }
.card-meta .name { font-size: 13px; line-height: 15.6px; }
.card-meta .sub { font-size: 13px; line-height: 15.6px; color: #888888; }
.empty { border: 1px dashed var(--border-menu); border-radius: var(--r-card); padding: 40px; text-align: center; color: var(--text-2); display: flex; flex-direction: column; align-items: center; gap: 12px; }

/* —— Barra superior (ficha 02) —— */
.topbar { height: var(--topbar-h); border-bottom: 1px solid var(--border); display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; padding: 0 10px; gap: 10px; background: var(--bg); position: relative; z-index: var(--z-ui); }
.topbar .left, .topbar .right { display: flex; align-items: center; gap: 10px; min-width: 0; }
.topbar .right { justify-content: flex-end; }
.topbar .center { display: flex; align-items: center; gap: 6px; min-width: 0; max-width: 40vw; }
.mode-button { height: 30px; border-radius: var(--r-control); background: var(--bg-selected); border: 0; display: inline-flex; align-items: center; gap: 8px; padding: 0 8px; cursor: pointer; flex-shrink: 0; }
.mode-button .mark { color: var(--text); flex-shrink: 0; }
.avatar { width: 30px; height: 30px; border-radius: var(--r-control); background: var(--bg-field); color: var(--text-2); font-weight: var(--fw-strong); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* —— Modo Settings (ficha 05): lista a la izquierda, contenido en el centro —— */
.settings { display: grid; grid-template-rows: var(--topbar-h) minmax(0, 1fr); height: 100%; }
.settings-body { display: grid; grid-template-columns: var(--sidebar-w) minmax(0, 1fr); min-height: 0; }
.settings-nav { border-right: 1px solid var(--border); padding: 10px; overflow: auto; z-index: var(--z-ui); }
.settings-nav .group { padding: 13px 5px 8px; color: var(--text); }
.settings-main { overflow: auto; min-width: 0; }
.settings-page { max-width: 640px; margin: 0 auto; padding: 32px 24px 64px; display: flex; flex-direction: column; gap: 32px; }
.section { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.section h2 { font-size: 16px; line-height: 20px; font-weight: var(--fw-strong); margin: 0; }
.section h3 { font-size: 12px; font-weight: var(--fw-strong); margin: 0; }
.panel { border-radius: var(--r-card); background: var(--bg-thumb); padding: 12px; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.list { display: flex; flex-direction: column; border-radius: var(--r-card); background: var(--bg-thumb); overflow: hidden; }
.list-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; min-height: 48px; border-top: 1px solid var(--border); min-width: 0; }
.list-row:first-child { border-top: 0; }
.table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.table th { text-align: left; font-weight: var(--fw); color: var(--text-3); padding: 6px 8px; }
.table td { padding: 4px 8px; vertical-align: middle; }
.dropzone { border: 1px dashed var(--border-menu); border-radius: var(--r-card); padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 10px; color: var(--text-2); text-align: center; }
.dropzone.over { border-color: var(--accent); background: var(--bg-accent-soft); }
.dns { display: grid; grid-template-columns: 70px 90px minmax(0, 1fr); gap: 6px 10px; align-items: center; }
.dns .h { color: var(--text-3); }
.qr { background: #fff; border-radius: 8px; padding: 8px; width: 168px; height: 168px; }
.qr svg { width: 100%; height: 100%; }
.codes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; font-family: var(--mono); }

@media (max-width: 760px) {
  .dash, .settings-body { grid-template-columns: minmax(0, 1fr); }
  .sidebar, .settings-nav { display: none; }
  .main-inner { padding: 24px 16px; }
  .grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 20px; }
  .field-row { grid-template-columns: minmax(0, 1fr); }
  /* Sin sitio para el nombre centrado: se quita para que nada se monte sobre los botones. */
  .topbar { grid-template-columns: auto minmax(0, 1fr); }
  .topbar .center, .topbar .avatar { display: none; }
}
